import React, { useState } from "react";
import classnames from "classnames";
import styles from "./CollapsibleGroup.module.scss";

export interface CollapsibleGroupProps {
    /** Название группы; в заголовке выводится как «<title> (N)» */
    title: string;
    /** Число элементов в группе */
    count: number;
    /** Свёрнута ли группа, когда состоянием управляет родитель; тогда нужен и `onToggle` */
    collapsed?: boolean;
    /** Начальное состояние, когда `collapsed` не передан (группа хранит его сама) */
    defaultCollapsed?: boolean;
    /** Нажатие на заголовок; получает новое значение свёрнутости */
    onToggle?: (collapsed: boolean) => void;
    children?: React.ReactNode;
    className?: string;
}

/**
 * Группа списка со сворачиваемым содержимым. Заголовок — кнопка с `aria-expanded`, зона касания
 * не меньше 44 px по высоте. Содержимое в свёрнутом виде не рендерится.
 *
 * Состояние: если передан `collapsed` — управляется снаружи (меняйте его в `onToggle`);
 * иначе группа хранит его сама, начиная с `defaultCollapsed`.
 */
export const CollapsibleGroup = ({
    title,
    count,
    collapsed,
    defaultCollapsed = false,
    onToggle,
    children,
    className,
}: CollapsibleGroupProps) => {
    const [inner, setInner] = useState(defaultCollapsed);
    const isCollapsed = collapsed ?? inner;

    const toggle = () => {
        const next = !isCollapsed;
        if (collapsed === undefined) setInner(next);
        onToggle?.(next);
    };

    return (
        <section className={classnames(styles.group, className)}>
            <button type="button" className={styles.header} aria-expanded={!isCollapsed} onClick={toggle}>
                <span className={classnames(styles.chevron, { [styles.open]: !isCollapsed })} aria-hidden="true">
                    ›
                </span>
                <span className={styles.title}>{`${title} (${count})`}</span>
            </button>
            {!isCollapsed && <div className={styles.content}>{children}</div>}
        </section>
    );
};
